/**
 * Curricula list — shared presentational view (SearchFilterBar + DataTable).
 * Used by admin-console (editable: `onNew` shows "+ New") and teacher-app
 * (read-only + `scope` limits the list to the teacher's subjects/grades).
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GRADE_OPTIONS, ALL_SUBJECT_OPTIONS, ALL_PROGRAMME_OPTIONS } from '@padcanvas/shared';
import { useSubjectLabel, programmeLabel } from '../reference-data';
import { DataTable, type ColumnDef, type SortState, SearchFilterBar, type FilterDef, Button, InfoTip } from '../components';
import type { CurriculumListItem, CurriculumScope } from './types';
import './curriculum-view.css';

// Filter keys persisted in the URL query string so the selection survives
// navigating into a curriculum and pressing Back (the list unmounts otherwise).
const FILTER_KEYS = ['subject', 'grade', 'board', 'status', 'owner', 'search'] as const;

/** Who authored it. `ownerType` is absent on everything written before the
 *  ownership model, and those are the shared library — so anything not
 *  explicitly a teacher's reads as system-owned, rather than 32 legacy rows
 *  matching neither filter. */
const isTeacherOwned = (c: { ownerType?: string }) => c.ownerType === 'teacher';

const fmtDate = (ts?: number) => (ts ? new Date(ts * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

/** Comparable value per sortable column. Empty grade sorts last; dates/counts numeric. */
const SORT_VALUE: Record<string, (c: CurriculumListItem) => string | number> = {
  name: (c) => c.name.toLowerCase(),
  board: (c) => c.board,
  grade: (c) => (c.grade ? Number(c.grade) : Number.MAX_SAFE_INTEGER),
  subject: (c) => c.subject,
  lastModified: (c) => c.lastModified ?? 0,
  questions: (c) => (c.questionCount ?? -1), // uncomputed sorts first (asc)
  coverage: (c) => (c.coverage ? c.coverage.lostPct : -1), // uncomputed sorts first (asc)
};

interface Props {
  items: CurriculumListItem[];
  /** When set, the list + filter options are limited to these subjects/grades. */
  scope?: CurriculumScope;
  onOpen: (id: string) => void;
  /** Admin only — shows the "+ New Curriculum" button. Omit for read-only. */
  onNew?: () => void;
  title?: string;
  infoText?: string;
  /** Extra content rendered inside the create/edit flow (admin modal), etc. */
  children?: ReactNode;
  /** Show who owns each curriculum, and let the reader filter by it. For a
   *  surface that sees both kinds — the admin console sees every teacher's as
   *  well as the shared library — where "whose is this" decides whether it may
   *  be edited. Off elsewhere: a teacher's list is their own plus the shared
   *  set, and a column saying so on every row earns nothing. */
  showOwnership?: boolean;
  /** Query state. Without these an empty `items` is indistinguishable from a
   *  fetch that is still running or has failed, and the table asserts "no
   *  curricula yet" in all three cases — which reads as a loaded empty list. */
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
}

export function CurriculaListView({ items, scope, onOpen, onNew, title = 'Curricula', infoText, children, showOwnership, loading, error, onRetry }: Props) {
  const subjectLabel = useSubjectLabel();
  // Filters live in the URL (?subject=…&status=… etc.) so Back from a detail
  // page restores them. Derive the filter map from the query string, and mirror
  // changes back (replace: true — filtering isn't a history entry).
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => {
    const f: Record<string, string> = {};
    for (const k of FILTER_KEYS) {
      const v = searchParams.get(k);
      if (v) f[k] = v;
    }
    return f;
  }, [searchParams]);
  const setFilters = useCallback((next: Record<string, string>) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      for (const k of FILTER_KEYS) {
        const v = (next[k] ?? '').trim();
        if (v) p.set(k, v);
        else p.delete(k);
      }
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  // Scope-limit the rows and the subject/grade dropdown options.
  const subjectOpts = scope?.subjects?.length
    ? ALL_SUBJECT_OPTIONS.filter((s) => scope.subjects!.includes(s))
    : ALL_SUBJECT_OPTIONS;
  const gradeOpts = scope?.grades?.length
    ? GRADE_OPTIONS.filter((g) => scope.grades!.includes(g))
    : GRADE_OPTIONS;

  const scoped = useMemo(() => {
    let r = items;
    if (scope?.subjects?.length) r = r.filter((c) => scope.subjects!.includes(c.subject));
    if (scope?.grades?.length) r = r.filter((c) => !c.grade || scope.grades!.includes(c.grade));
    return r;
  }, [items, scope]);

  // The programmes actually present, in canonical order — derived, not a static
  // list. A fixed base-five list hid the US courses that exist (they were in the
  // table while the filter denied they were there); the full vocabulary instead
  // would offer AP to a teacher who has no AP course. What is filterable is
  // exactly what is on screen.
  const boardOpts = useMemo(() => {
    const present = new Set(scoped.map((c) => c.board).filter(Boolean) as string[]);
    return ALL_PROGRAMME_OPTIONS.filter((p) => present.has(p));
  }, [scoped]);

  const visible = useMemo(() => {
    let r = scoped;
    if (filters.subject) r = r.filter((c) => c.subject === filters.subject);
    if (filters.grade) r = r.filter((c) => c.grade === filters.grade);
    if (filters.board) r = r.filter((c) => c.board === filters.board);
    if (filters.owner === 'teacher') r = r.filter(isTeacherOwned);
    else if (filters.owner === 'system') r = r.filter((c) => !isTeacherOwned(c));
    if (filters.status === 'active') r = r.filter((c) => c.isActive !== false);
    else if (filters.status === 'inactive') r = r.filter((c) => c.isActive === false);
    const q = (filters.search || '').trim().toLowerCase();
    if (q) r = r.filter((c) => c.name.toLowerCase().includes(q) || c.id.toLowerCase().includes(q));
    return r;
  }, [scoped, filters]);

  const [sort, setSort] = useState<SortState>({ sortBy: 'name', sortOrder: 'asc' });
  const sorted = useMemo(() => {
    const fn = SORT_VALUE[sort.sortBy];
    if (!fn) return visible;
    const dir = sort.sortOrder === 'asc' ? 1 : -1;
    return [...visible].sort((a, b) => {
      const va = fn(a), vb = fn(b);
      const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return cmp * dir;
    });
  }, [visible, sort]);

  /**
   * Is a grade meaningful for what is on screen?
   *
   * A course has none — "AP Calculus AB" is a course, not a class — so filtering
   * to AP left a column of dashes taking width and saying nothing. Derived from
   * the visible rows rather than from the programme, so it covers exam tracks
   * and any future gradeless programme without naming any of them.
   */
  const showGrade = visible.some((c) => !!c.grade);

  const filterDefs: FilterDef[] = [
    { key: 'subject', placeholder: 'All subjects', options: subjectOpts.map((s) => ({ value: s, label: subjectLabel(s) })) },
    { key: 'grade', placeholder: 'All grades', options: gradeOpts.map((c) => ({ value: c, label: `Class ${c}` })) },
    { key: 'board', placeholder: 'All boards', options: boardOpts.map((t) => ({ value: t, label: programmeLabel(t) })) },
    { key: 'status', placeholder: 'All statuses', options: [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }] },
    ...(showOwnership ? [{
      key: 'owner', placeholder: 'All owners',
      options: [{ value: 'system', label: 'System owned' }, { value: 'teacher', label: 'Teacher owned' }],
    }] : []),
  ];

  const num = (n: number) => n || '—';
  const columns: ColumnDef<CurriculumListItem>[] = [
    // Active/inactive is carried by the row's dimming (`cv-row-inactive`) rather
    // than a column — nearly every row is active, so a column of identical
    // "Active" pills cost width without telling the reader anything. The status
    // filter above still selects on it.
    { key: 'name', header: 'Name', render: (c) => <strong>{c.name}</strong>, mobile: 'title' },
    { key: 'board', header: 'Board', render: (c) => c.board, sortable: true, mobileLabel: 'Board' },
    ...(showGrade ? [{
      key: 'grade', header: 'Grade', sortable: true,
      render: (c: CurriculumListItem) => (c.grade ? `Class ${c.grade}` : '—'),
    }] : []),
    { key: 'subject', header: 'Subject', render: (c) => subjectLabel(c.subject), sortable: true },
    ...(showOwnership ? [{
      key: 'owner', header: 'Owner', sortable: true,
      sortValue: (c: CurriculumListItem) => (isTeacherOwned(c) ? 'teacher' : 'system'),
      render: (c: CurriculumListItem) => (isTeacherOwned(c) ? 'Teacher' : 'System'),
    }] : []),
    { key: 'lastModified', header: 'Last modified', render: (c) => fmtDate(c.lastModified), sortable: true },
    {
      key: 'structure', header: 'Structure', mobile: 'hide',
      render: (c) => {
        const parts: string[] = [];
        if (c.counts?.unit) parts.push(`${c.counts.unit} U`);
        if (c.counts?.chapter) parts.push(`${c.counts.chapter} Ch`);
        if (c.counts?.section) parts.push(`${c.counts.section} Sec`);
        if (!parts.length) return '—';
        // A structure that looks complete can still be unpractisable: chapters
        // with too few questions behind them. Flagged here, next to the chapter
        // count it qualifies. Like Questions and Lost %, the stat only exists
        // once the curriculum has been opened — no badge means not computed,
        // which is why a zero count renders nothing rather than an "all clear".
        const thin = c.thinChapters;
        return (
          <span className="cv-structure">
            <span title="Units · Chapters · Sections">{parts.join(' · ')}</span>
            {thin && thin.count > 0 && (
              <span
                className="cv-thin-warn"
                aria-label={`${thin.count} thin chapters`}
                title={`${thin.count} of ${thin.total} chapters have fewer than ${thin.threshold} questions`}
              >
                ⚠
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'questions', header: 'Questions', sortable: true,
      render: (c) => (c.questionCount != null
        ? num(c.questionCount)
        : <span style={{ color: 'var(--text-tertiary, #999)' }} title="Open the curriculum to compute">—</span>),
    },
    {
      key: 'coverage', header: 'Lost %', sortable: true,
      render: (c) => {
        if (!c.coverage) return <span style={{ color: 'var(--text-tertiary, #999)' }} title="Open the curriculum to compute">—</span>;
        const { lostPct, lostCount, total, computedAt } = c.coverage;
        const ok = lostPct <= 5; // matches the ≤5% "ready" threshold
        return (
          <span title={`${lostCount}/${total} in-scope questions not reachable via this curriculum`}
            style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.2 }}>
            <strong style={{ color: ok ? 'var(--color-success)' : 'var(--color-error)' }}>{lostPct.toFixed(1)}%</strong>
            <span style={{ fontSize: 10, color: 'var(--text-tertiary, #999)' }}>as of {fmtDate(Math.floor(Date.parse(computedAt) / 1000))}</span>
          </span>
        );
      },
    },
  ];

  return (
    <div className="settings-section">
      <div className="section-header">
        <div className="section-header-top">
          <h2>{title} {infoText && <InfoTip text={infoText} />}</h2>
        </div>
      </div>

      <div className="cv-list-toolbar">
        <div className="cv-list-toolbar-filters">
          <SearchFilterBar filters={filterDefs} values={filters} onChange={setFilters} searchPlaceholder="Search by name / id…" hideFetch />
        </div>
        {onNew && <Button variant="primary" size="sm" onClick={onNew}>+ New Curriculum</Button>}
      </div>

      {error ? (
        // Shown instead of the table: an empty table under an error banner still
        // says "no curricula yet", and that sentence is the thing to retract.
        <div className="cv-load-error" role="alert">
          <span>Could not load curricula. {(error as Error)?.message ?? ''}</span>
          {onRetry && <Button variant="secondary" size="sm" onClick={onRetry}>Retry</Button>}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={sorted}
          rowKey={(c) => c.id}
          rowClassName={(c) => (c.isActive === false ? 'cv-row-inactive' : '')}
          onRowClick={(c) => onOpen(c.id)}
          sort={sort}
          onSortChange={setSort}
          loading={loading}
          emptyMessage={scope ? 'No curricula for your subjects/grades yet.' : 'No curricula yet — create one to begin.'}
          compact
          bordered
          mobileCards
        />
      )}

      {children}
    </div>
  );
}
